// The floor-planning scene root: a "sea of dots" — a real-world metres grid
// drawn as uniform DOTS (1 m spacing), with NO ground plane behind it. Lives in
// world units (1 unit = 1 metre). Hidden in modeling mode. Walls / placed models
// get added under `root`. The dots fade out with distance FROM THE SCENE, so the
// layout sits on a clear "island" of dots that fades into the dark beyond it.

import * as THREE from "three";

const FIELD_SIZE = 120; // metres — large enough to read as an open field
const STEP = 1; // metres between dots

export interface FloorScene {
  root: THREE.Group;
  // Center the dot "island" on the scene and set its clear radius / falloff
  // (metres). Dots within `fadeStart` of (cx,cz) are full strength; they fade to
  // nothing by `fadeEnd`. Also re-aims the presentation sun + shadow frustum at
  // the same island.
  setGridIsland(cx: number, cz: number, fadeStart: number, fadeEnd: number): void;
  // Presentation lighting (VR walkthrough / Layout "Lighting" preview): swap
  // the dot field for a real ground plane and turn the shadow-casting sun on.
  setPresentation(on: boolean): void;
  // Show/hide just the metres dot-sea, independent of the presentation rig. The
  // AR layout tabletop hides it: a 1 m-spacing grid shrunk onto a desk reads as
  // noise, and there's a real desk under the layout in passthrough anyway.
  setDotField(on: boolean): void;
  // Show the ground disc as the AR tabletop's room floor: centered on the layout
  // at (cx,cz) with the given radius (floor-local metres), so the layout stands
  // on a surface instead of floating over the passthrough desk. Its radial alpha
  // fades the floor out past the layout rather than ending in a hard edge. Reuses
  // the presentation ground mesh (the two modes never run at once). on=false hides
  // it.
  setGroundDisc(on: boolean, cx?: number, cz?: number, radius?: number): void;
}

export function createFloorScene(): FloorScene {
  const root = new THREE.Group();
  root.visible = false;

  // A uniform grid of dots, one every STEP metres.
  const half = FIELD_SIZE / 2;
  const n = Math.round(FIELD_SIZE / STEP);
  const positions: number[] = [];
  for (let a = 0; a <= n; a++) {
    const x = -half + a * STEP;
    for (let b = 0; b <= n; b++) {
      positions.push(x, 0, -half + b * STEP);
    }
  }
  const geo = new THREE.BufferGeometry();
  geo.setAttribute("position", new THREE.Float32BufferAttribute(positions, 3));

  const mat = new THREE.ShaderMaterial({
    transparent: true,
    depthWrite: false, // walls occlude the dots, but they don't self-occlude
    uniforms: {
      uColor: { value: new THREE.Color(0x99a2c0) },
      uSize: { value: 3.6 }, // CSS px
      uOpacity: { value: 1.0 },
      uPixelRatio: { value: Math.min(window.devicePixelRatio || 1, 2) },
      uCenter: { value: new THREE.Vector2(0, 0) }, // island center (world XZ)
      uFadeStart: { value: 18.0 }, // metres from center where dots stay full
      uFadeEnd: { value: 38.0 }, // ...and where they're fully gone
    },
    vertexShader: /* glsl */ `
      varying float vDist;
      uniform vec2 uCenter;
      uniform float uSize;
      uniform float uPixelRatio;
      void main() {
        vec3 wpos = (modelMatrix * vec4(position, 1.0)).xyz;
        vDist = length(wpos.xz - uCenter); // horizontal distance FROM THE SCENE
        gl_Position = projectionMatrix * modelViewMatrix * vec4(position, 1.0);
        gl_PointSize = uSize * uPixelRatio;
      }
    `,
    fragmentShader: /* glsl */ `
      varying float vDist;
      uniform vec3 uColor;
      uniform float uOpacity;
      uniform float uFadeStart;
      uniform float uFadeEnd;
      void main() {
        vec2 c = gl_PointCoord - 0.5;
        if (dot(c, c) > 0.25) discard; // round dot, not a square
        float a = uOpacity * (1.0 - smoothstep(uFadeStart, uFadeEnd, vDist));
        if (a < 0.01) discard;
        gl_FragColor = vec4(uColor, a);
      }
    `,
  });

  const dots = new THREE.Points(geo, mat);
  root.add(dots);

  // ── Presentation rig furniture ────────────────────────────────────────
  // Both live under `root` ON PURPOSE: in the VR walkthrough, teleports move
  // root around the origin-fixed player. With the sun parented inside, light
  // and room move rigidly together, so a shadow map baked once at walkthrough
  // entry stays valid through every teleport (relative geometry is unchanged).

  // Ground disc the sun shadows fall on (the dot field has no surface). Like
  // the dots it's an "island": a radial alpha gradient fades it into the dark
  // beyond the layout instead of ending at a hard horizon line. Unit circle,
  // scaled/centered to the island by setGridIsland. Sits a hair below y=0 so
  // the wall bottoms never z-fight it.
  const groundAlpha = (() => {
    const c = document.createElement("canvas");
    c.width = c.height = 256;
    const g = c.getContext("2d")!;
    const grad = g.createRadialGradient(128, 128, 0, 128, 128, 128);
    grad.addColorStop(0, "#fff");
    grad.addColorStop(0.55, "#fff"); // full strength over the layout...
    grad.addColorStop(1, "#000"); // ...gone by the island edge
    g.fillStyle = grad;
    g.fillRect(0, 0, 256, 256);
    return new THREE.CanvasTexture(c);
  })();
  const ground = new THREE.Mesh(
    new THREE.CircleGeometry(1, 64).rotateX(-Math.PI / 2),
    new THREE.MeshStandardMaterial({
      color: 0x2e3340,
      roughness: 0.95,
      alphaMap: groundAlpha,
      transparent: true,
    }),
  );
  ground.position.y = -0.002;
  ground.scale.set(32, 1, 32); // matches the empty-layout island until setGridIsland runs
  ground.receiveShadow = true;
  ground.visible = false;
  root.add(ground);

  // Intensity is tuned against the LOW ambient in main's presentation rig
  // (env 0.5 + hemi 0.3): direct sun ≈ 3× ambient, so roofed interiors read
  // clearly darker than sunlit exteriors and window patches pop.
  const sun = new THREE.DirectionalLight(0xfff1dc, 2.4);
  sun.castShadow = true;
  sun.shadow.mapSize.set(2048, 2048);
  sun.shadow.normalBias = 0.03; // box geometry: kills acne without peter-panning
  sun.visible = false;
  root.add(sun, sun.target);
  // Re-aim the sun at the layout island and size its shadow frustum to cover
  // it. `half` tracks the island's full-strength radius (layout extent + a
  // margin — see the setGridIsland callers).
  const aimSun = (cx: number, cz: number, half: number): void => {
    sun.target.position.set(cx, 0, cz);
    // ~35° elevation: low enough that sunlight streams through wall openings
    // and throws window-shaped patches across interior floors (the roof —
    // walls.ts setCeilingMode — blocks everything else), high enough that the
    // shadow run stays inside the frustum.
    sun.position.set(cx + half * 1.15, half * 0.9, cz + half * 0.6);
    const cam = sun.shadow.camera;
    cam.left = -half;
    cam.right = half;
    cam.top = half;
    cam.bottom = -half;
    cam.near = 0.5;
    cam.far = half * 4;
    cam.updateProjectionMatrix();
  };
  aimSun(0, 0, 14);

  return {
    root,
    setGridIsland(cx: number, cz: number, fadeStart: number, fadeEnd: number): void {
      mat.uniforms.uCenter.value.set(cx, cz);
      mat.uniforms.uFadeStart.value = fadeStart;
      mat.uniforms.uFadeEnd.value = fadeEnd;
      ground.position.set(cx, -0.002, cz);
      ground.scale.set(fadeEnd, 1, fadeEnd);
      aimSun(cx, cz, fadeStart);
    },
    setPresentation(on: boolean): void {
      dots.visible = !on;
      ground.visible = on;
      sun.visible = on;
    },
    setDotField(on: boolean): void {
      dots.visible = on;
    },
    setGroundDisc(on: boolean, cx?: number, cz?: number, radius?: number): void {
      ground.visible = on;
      if (on && cx !== undefined && cz !== undefined && radius !== undefined) {
        ground.position.set(cx, -0.002, cz);
        ground.scale.set(radius, 1, radius);
      }
    },
  };
}
